import { useQuery } from '@apollo/client/react';

import { ViewerDocument } from '../../../graphql/__generated__/types';

/**
 * What the portal knows of the signed-in person's account attachments. Loading, failed
 * and genuinely unattached look alike but must stay distinct: an error shown as an
 * absence sends someone to claim access they already hold.
 */
export type ViewerAttachment =
    { status: 'loading' } | { status: 'failed'; retry: () => void } | { status: 'attached' } | { status: 'unattached' };

/**
 * Single reader of "is this person attached to any account?", shared by the home page
 * and the menu so the two do not diverge. Not a security guard: the perimeter is a
 * server rule; this only avoids showing a blank page that reads as a broken portal.
 */
const useViewerAttachment = (): ViewerAttachment => {
    const { data, loading, error, refetch } = useQuery(ViewerDocument);

    // Do not refilter on `membership.status`: the API already returns only the
    // memberships that count (`active` and `invited`); a second filter here would make
    // portal and back-office report different counts for the same person.
    const memberships = data?.viewer?.memberships ?? [];

    // Order matters: a membership in hand decides, even under a partial error; only
    // then may we not know, and "unattached" comes last, on an empty error-free
    // response. `viewer` itself can be null (no row in our `user` table), read the same.
    if (memberships.length > 0) {
        return { status: 'attached' };
    }
    if (loading) {
        return { status: 'loading' };
    }
    if (error) {
        return {
            status: 'failed',
            retry: () => {
                void refetch();
            },
        };
    }
    return { status: 'unattached' };
};

export default useViewerAttachment;
